/**
 * CommunitiesPage — Browse and join food communities
 * Route: /communities
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Users, Plus, Search, ChevronRight, MessageCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_COLORS = {
  Vegan: 'from-green-400 to-emerald-600', Keto: 'from-purple-400 to-violet-600', Halal: 'from-teal-400 to-cyan-600',
  Italian: 'from-red-400 to-rose-600', Japanese: 'from-pink-400 to-fuchsia-600', Thai: 'from-amber-400 to-orange-600',
  'Street Food': 'from-yellow-400 to-amber-600', 'Home Cooking': 'from-lime-400 to-green-600', General: 'from-gray-400 to-gray-600',
};

const CommunitiesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCat, setNewCat] = useState('General');

  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const fetchCommunities = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchQ) params.append('q', searchQ);
    if (category) params.append('category', category);
    fetch(`${API_URL}/api/communities?${params}`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(d => { setCommunities(d.communities || []); setCategories(d.categories || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCommunities(); }, [searchQ, category]);

  const handleJoin = async (id) => {
    if (!user) { navigate('/profile'); return; }
    await fetch(`${API_URL}/api/communities/${id}/join`, { method: 'POST', credentials: 'include', headers });
    fetchCommunities();
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await fetch(`${API_URL}/api/communities`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
      body: JSON.stringify({ name: newName, description: newDesc, category: newCat }),
    });
    setShowCreate(false); setNewName(''); setNewDesc('');
    fetchCommunities();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      <div className="relative" style={{ minHeight: '180px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF8C42] to-[#F4C430]" />
        <div className="relative flex flex-col justify-end max-w-4xl mx-auto px-4" style={{ minHeight: '180px' }}>
          <div className="mb-5">
            <Link to="/" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs mb-2"><ArrowLeft className="w-3 h-3" /> Back</Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Users className="w-8 h-8" /> Communities</h1>
            <p className="text-white/70 text-sm mt-1">Join food groups for your interests</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        <div className="flex gap-3 mb-5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search communities..."
              className="w-full pl-10 pr-4 py-2.5 bg-white rounded-2xl text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30" />
          </div>
          {user && <button onClick={() => setShowCreate(!showCreate)} className="h-10 px-4 bg-gray-900 text-white rounded-2xl text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" />Create</button>}
        </div>

        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-5">
          <button onClick={() => setCategory('')} className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${!category ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>All</button>
          {categories.slice(0, 10).map(c => (
            <button key={c} onClick={() => setCategory(category === c ? '' : c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${category === c ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>
          ))}
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="bg-white rounded-[20px] p-5 mb-5 shadow-sm">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Create Community</h3>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Community name" className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30" />
            <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description..." rows={2} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-2 resize-none focus:outline-none" />
            <select value={newCat} onChange={e => setNewCat(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm mb-3">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={handleCreate} className="w-full py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-2xl">Create</button>
          </div>
        )}

        {/* Community grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-100 rounded-[20px] animate-pulse" />)}</div>
        ) : communities.length === 0 ? (
          <div className="text-center py-16"><Users className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-bold text-gray-700">No communities yet</h3><p className="text-sm text-gray-500">Create the first one!</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {communities.map(c => (
              <div key={c.community_id} className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-md transition-all">
                <div className={`h-16 bg-gradient-to-r ${CATEGORY_COLORS[c.category] || CATEGORY_COLORS.General}`} />
                <div className="p-4 -mt-6">
                  <Avatar className="w-12 h-12 ring-4 ring-white mb-2">
                    <AvatarFallback className={`bg-gradient-to-br ${CATEGORY_COLORS[c.category] || CATEGORY_COLORS.General} text-white text-lg font-bold`}>{c.name[0]}</AvatarFallback>
                  </Avatar>
                  <h3 className="text-sm font-bold text-gray-900">{c.name}</h3>
                  <p className="text-xs text-gray-500 line-clamp-1 mb-2">{c.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.member_count}</span>
                      <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{c.post_count}</span>
                    </div>
                    <button onClick={() => c.is_member ? navigate(`/communities/${c.community_id}`) : handleJoin(c.community_id)}
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${c.is_member ? 'bg-[#FFF5F0] text-[#FF6B35]' : 'bg-gray-900 text-white'}`}>
                      {c.is_member ? 'Open' : 'Join'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunitiesPage;
